import type { ColumnDef } from '@tanstack/react-table';

import { Badge } from '@cold-ambulance/shadcn';
import { formatDateTime, toAge } from '@cold-ambulance/ui';

export function phoneNumberColumn<
  T extends { phoneNumber?: string },
>(): ColumnDef<T>;
export function phoneNumberColumn<T>(
  accessor: (row: T) => { phoneNumber?: string } | null | undefined,
): ColumnDef<T>;
export function phoneNumberColumn<T extends { phoneNumber?: string }>(
  accessor?: (row: T) => { phoneNumber?: string } | null | undefined,
): ColumnDef<T> {
  return {
    id: 'phoneNumber',
    header: 'Phone Number',
    enableSorting: false,
    cell: ({ row }) => {
      const original = accessor ? accessor(row.original) : row.original;
      const phoneNumber = original?.phoneNumber;
      return (
        <div className="font-mono">
          {phoneNumber || <span className="text-muted-foreground">N/A</span>}
        </div>
      );
    },
  };
}

export function emailColumn<T extends { email: string }>(): ColumnDef<T> {
  return {
    accessorKey: 'email',
    header: 'Email',
    cell: ({ row }) => <div className="lowercase">{row.getValue('email')}</div>,
  };
}

export function createdAtColumn<
  T extends { createdAt: string },
>(): ColumnDef<T> {
  return {
    accessorKey: 'createdAt',
    header: 'Created At',
    cell: ({ row }) => (
      <div className="text-sm">{formatDateTime(row.original.createdAt)}</div>
    ),
  };
}

export function updatedAtColumn<
  T extends { updatedAt: string },
>(): ColumnDef<T> {
  return {
    accessorKey: 'updatedAt',
    header: 'Last Updated',
    cell: ({ row }) => (
      <div className="text-muted-foreground text-sm">
        {formatDateTime(row.original.updatedAt)}
      </div>
    ),
  };
}

export function fullNameColumn<
  T extends { firstName?: string; lastName?: string },
>(): ColumnDef<T> {
  return {
    id: 'name',
    accessorFn: (row) => `${row.firstName || ''} ${row.lastName || ''}`.trim(),
    header: 'Name',
    cell: ({ row }) => {
      const firstName = row.original.firstName || '';
      const lastName = row.original.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim();
      return <div className="font-medium">{fullName}</div>;
    },
  };
}
export function nameColumn<T extends { name: string }>(): ColumnDef<T> {
  return {
    id: 'name',
    header: 'Name',
    cell: ({ row }) => {
      return <div className="font-medium">{row.original.name}</div>;
    },
  };
}

export function genderColumn<T extends { gender?: string }>(): ColumnDef<T>;
export function genderColumn<T>(
  accessor: (row: T) => { gender?: string } | undefined,
): ColumnDef<T>;
export function genderColumn<T extends { gender?: string }>(
  accessor?: (row: T) => { gender?: string } | undefined,
): ColumnDef<T> {
  return {
    accessorKey: 'gender',
    header: 'Gender',
    cell: ({ row }) => {
      const original = accessor ? accessor(row.original) : row.original;
      const gender = original?.gender;
      return (
        <div className="capitalize">
          {gender ? (
            <Badge variant="outline">{gender}</Badge>
          ) : (
            <span className="text-muted-foreground">N/A</span>
          )}
        </div>
      );
    },
  };
}

export function ageColumn<T extends { birthDate?: string }>(): ColumnDef<T>;
export function ageColumn<T>(
  accessor: (row: T) => { birthDate?: string } | undefined,
): ColumnDef<T>;
export function ageColumn<T extends { birthDate?: string }>(
  accessor?: (row: T) => { birthDate?: string } | undefined,
): ColumnDef<T> {
  return {
    accessorKey: 'birthDate',
    header: 'Age',
    cell: ({ row }) => {
      const original = accessor ? accessor(row.original) : row.original;
      return original?.birthDate ? (
        <div>{toAge(original)} years</div>
      ) : (
        <span className="text-muted-foreground">N/A</span>
      );
    },
  };
}
